import React, { useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import * as Styled from './styles';
import { toast } from 'react-toastify';
import { useLiveQuery } from 'dexie-react-hooks';
import { IoIosLock } from 'react-icons/io';
import { IoMdCreate } from 'react-icons/io';
import { BsFileEarmarkCode } from 'react-icons/bs';
import { Button, Input, InputPhoneIntl } from '@praticabr/ppsa';
import z from 'zod';
import { useForm } from 'react-hook-form';
import { db } from '../../../../../../db/db';
import { zodResolver } from '@hookform/resolvers/zod';
import Loading from '../../../../../Loading';
import Modal from '../../../../../UIelements/Modal';
import { changeAccountEmail, editAccount } from '../../../../../../services';
import { terminateIokSession } from '../../../../../../integrations/iokSessionLifecycle';

type ManualError = {
    type: string | "manual";
    message: string;
};

const createError = (message: string, type?: string,): ManualError => ({
    type: type || "manual",
    message
});

type ModalProps = {
    user: any;
    onCancel: () => void;
    onChangePage: (page: string) => void;
    enableEmailField: boolean;
    currentPassword?: string;
}

const editAccountFormSchema = z.object({
    userName: z.string().nonempty('user.Preencha o nome do usuário').trim(),
    email: z.email('user.E-mail inválido').nonempty('user.Preencha o e-mail do usuário'),
    phone: z
        .string()
        .trim()
        .nonempty("user.Preencha o telefone do usuário")
        .refine(
            (value) => {
                const v = value.trim();
                if (/^\+\d{1,3}$/.test(v)) {
                    return false;
                }

                return true;
            },
            {
                message: "user.Preencha o telefone do usuário",
            }
        ),
    id: z.number().optional(),
});

type FormDataEditAccount = z.infer<typeof editAccountFormSchema>;

export const ModalEditProfile: React.FC<ModalProps> = (props) => {
    const intl = useIntl();
    const userDb = useLiveQuery(() => db.user.toArray());
    const [phone, setPhone] = useState<any>(props.user.phone);
    const [isLoading, setIsLoading] = useState(false);
    const [pendingEmailChange, setPendingEmailChange] = useState<FormDataEditAccount>();
    const emailChangeInFlight = useRef(false);


    const { register, handleSubmit, formState: { errors } } = useForm<FormDataEditAccount>({
        resolver: zodResolver(editAccountFormSchema),
        defaultValues: {
            userName: props.user.userName,
            email: props.user.email,
            phone: props.user.phone,

        }
    })


    const onSubmit = (data: FormDataEditAccount) => {
        const emailChanged = data.email.trim().toLocaleLowerCase('en-US') !== props.user.email.trim().toLocaleLowerCase('en-US');
        if (emailChanged) {
            if (!props.currentPassword) {
                props.onChangePage('InformPassword');
                return;
            }
            setPendingEmailChange(data);
            return;
        }

        if (data && userDb?.[0]) {
            data.id = userDb[0].id;
            setIsLoading(true);
            editAccount(data, userDb[0].accessToken)
                .then(resp => {
                    setTimeout(function () {
                        db.user.update(props.user.id, resp.data.editedUserData)
                        toast.success(intl.formatMessage({ id: 'main.Conta editada com sucesso!' }).toUpperCase());
                        props.onCancel();
                    }, 250);
                })
                .catch(error => {
                    console.error(error);
                    toast.error(intl.formatMessage({ id: 'menus.Existem campos com informações inválidas' }).toUpperCase());
                }).finally(() => {
                    setIsLoading(false)
                });
        }
    };

    const confirmEmailChange = async () => {
        if (emailChangeInFlight.current || !pendingEmailChange || !userDb?.[0] || !props.currentPassword) return;
        emailChangeInFlight.current = true;
        setIsLoading(true);
        try {
            const response = await changeAccountEmail({
                newEmail: pendingEmailChange.email,
                currentPassword: props.currentPassword,
                userName: pendingEmailChange.userName,
                phone: pendingEmailChange.phone
            }, userDb[0].accessToken);
            const activationStatus = response.data.activationEmailSent ? 'email-changed' : 'email-delivery-failed';
            await terminateIokSession({
                reason: 'user-change',
                publicPath: `/login?accountActivation=${activationStatus}`
            });
        } catch (error: any) {
            const code = error?.response?.data?.code;
            if (error?.response?.status === 401) {
                toast.error(intl.formatMessage({ id: 'main.Informe sua senha atual corretamente' }).toUpperCase());
            } else if (code === 'EMAIL_IN_USE') {
                toast.error(intl.formatMessage({ id: 'user.E-mail já cadastrado na plataforma. Informe um novo e-mail' }).toUpperCase());
            } else if (code === 'IDENTITY_SYNC_IN_PROGRESS') {
                toast.error(intl.formatMessage({ id: 'accountEmailChange.identityBusy' }).toUpperCase());
            } else {
                toast.error(intl.formatMessage({ id: 'accountEmailChange.changeFailed' }).toUpperCase());
            }
            emailChangeInFlight.current = false;
            setIsLoading(false);
        }
    };

    const cancelEmailChange = () => {
        if (emailChangeInFlight.current) return;
        setPendingEmailChange(undefined);
    };


    const footer = (
        <>
            <Button type='button' typeof='submit' size='lg' variant='confirmation-modal' width='108px' onClick={handleSubmit(onSubmit)} >
                {intl.formatMessage({ id: 'main.SALVAR' })}
            </Button>
            <Button type='button' size='lg' variant='cancellation' width='108px' onClick={() => props.onCancel()} >
                {intl.formatMessage({ id: 'menus.CANCELAR' })}
            </Button>
        </>
    );


    if (pendingEmailChange) {
        return (
            <Modal
                title={intl.formatMessage({ id: 'accountEmailChange.confirmTitle' })}
                onCancel={cancelEmailChange}
                footer={(
                    <>
                        <Button type='button' size='lg' variant='confirmation-modal' disabled={isLoading} onClick={confirmEmailChange}>
                            {intl.formatMessage({ id: 'accountEmailChange.confirmAction' })}
                        </Button>
                        <Button type='button' size='lg' variant='cancellation' disabled={isLoading} onClick={cancelEmailChange}>
                            {intl.formatMessage({ id: 'menus.CANCELAR' })}
                        </Button>
                    </>
                )}
                width='380px'
            >
                {isLoading
                    ? <Loading />
                    : <Styled.ConfirmationText>{intl.formatMessage({ id: 'accountEmailChange.confirmMessage' }, { email: pendingEmailChange.email })}</Styled.ConfirmationText>}
            </Modal>
        );
    }

    return (
        <Modal
            title={intl.formatMessage({ id: 'main.EDITAR CONTA' })}
            onCancel={props.onCancel}
            footer={footer}
            width='650px'
        >
            {isLoading && <Loading />}
            {!isLoading && (
                <>
                    <Styled.Div>
                        <Styled.Name className='name' >
                            <Input
                                id='userName'
                                label={intl.formatMessage({ id: 'main.Nome' }) + ' *'}
                                type='text'
                                {...register("userName")}
                                errors={errors.userName && createError(intl.formatMessage({ id: 'main.Preencha com o seu nome' }))}
                            />
                        </Styled.Name>
                        <Styled.Phone className='phone' >
                            <InputPhoneIntl
                                label={intl.formatMessage({ id: 'main.Telefone' }) + ' *'}
                                {...register("phone")}
                                value={phone}
                                onChange={setPhone}
                                errors={errors.phone && createError(intl.formatMessage({ id: 'main.Preencha com o seu telefone' }))}
                            />
                        </Styled.Phone>

                        <Styled.Email>
                            <Styled.Icon>
                                <Input
                                    id='email'
                                    label={intl.formatMessage({ id: 'main.E-mail' }) + ' *'}
                                    {...register("email")}
                                    type='text'
                                    errors={errors.email && createError(intl.formatMessage({ id: 'main.Preencha com o seu e-mail' }))}
                                    disabled={!props.enableEmailField}
                                />
                                {!props.enableEmailField && (
                                    <Styled.EditEmailButton
                                        type='button'
                                        aria-label={intl.formatMessage({ id: 'accountEmailChange.confirmTitle' })}
                                        onClick={() => props.onChangePage("InformPassword")}
                                    >
                                        <IoMdCreate aria-hidden='true' />
                                    </Styled.EditEmailButton>
                                )}
                            </Styled.Icon>
                        </Styled.Email>

                        <Styled.Div className='options' >
                            <Styled.DivOption>
                                <IoIosLock />
                                <p onClick={() => { props.onChangePage("ChangePassword") }}>{intl.formatMessage({ id: 'main.Alterar Senha' })}</p>
                            </Styled.DivOption>
                            {userDb && userDb[0].userTypeId == '1' &&
                                <Styled.DivOption>
                                    <BsFileEarmarkCode />
                                    <p onClick={() => { props.onChangePage("DeveloperToken") }}>{intl.formatMessage({ id: 'main.Token Desenvolvedor' })}</p>
                                </Styled.DivOption>}
                        </Styled.Div>
                    </Styled.Div>

                </>
            )}

        </Modal>
    )
}
